(() => {
  const candidateSelector = '[data-carousel-fallback-candidate]';
  const availabilitySelector = '[data-carousel-availability]';
  const deferredAttribute = 'data-carousel-deferred';

  class CarouselFallback extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.candidates = Array.from(this.querySelectorAll(candidateSelector));
      this.activeIndex = -1;
      this.selectedBlockId = '';
      this.onAvailabilityChange = this.onAvailabilityChange.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);

      this.addEventListener('carousel:availability-changed', this.onAvailabilityChange);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      if (this.candidates.length === 0) return;
      this.deferPendingCandidates();
      this.activateCandidate(0);
    }

    disconnectedCallback() {
      this.removeEventListener('carousel:availability-changed', this.onAvailabilityChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      this.initialized = false;
    }

    availabilityElement(candidate) {
      return candidate?.querySelector(availabilitySelector) || null;
    }

    availability(candidate) {
      return this.availabilityElement(candidate)?.dataset.carouselAvailability || 'empty';
    }

    deferPendingCandidates() {
      this.candidates.forEach((candidate) => {
        const availabilityElement = this.availabilityElement(candidate);
        if (availabilityElement?.dataset.carouselAvailability === 'pending') {
          availabilityElement.setAttribute(deferredAttribute, '');
        }
      });
    }

    concealCandidates() {
      this.candidates.forEach((candidate) => {
        candidate.hidden = true;
        candidate.querySelector('[data-carousel-fallback-editor-empty]')?.setAttribute('hidden', '');
      });
    }

    activateCandidate(index) {
      if (index < 0 || index >= this.candidates.length) {
        this.activeIndex = -1;
        this.hidden = true;
        return;
      }

      const candidate = this.candidates[index];
      const availabilityElement = this.availabilityElement(candidate);
      const availability = this.availability(candidate);

      this.activeIndex = index;
      this.concealCandidates();

      if (!availabilityElement || availability === 'empty') {
        if (window.Shopify?.designMode && this.selectedBlockId === candidate.dataset.carouselFallbackBlockId) {
          this.showEditorEmpty(candidate);
          return;
        }

        this.activateCandidate(index + 1);
        return;
      }

      if (availability === 'ready') {
        this.showCandidate(candidate);
        return;
      }

      this.hidden = true;
      availabilityElement.removeAttribute(deferredAttribute);
      candidate.dispatchEvent(new CustomEvent('carousel:fallback-activate', { bubbles: true }));
    }

    showCandidate(candidate) {
      this.hidden = false;
      candidate.hidden = false;
      candidate.querySelector('[data-carousel-fallback-editor-empty]')?.setAttribute('hidden', '');
    }

    showEditorEmpty(candidate) {
      this.hidden = false;
      candidate.hidden = false;
      candidate.querySelector('[data-carousel-fallback-editor-empty]')?.removeAttribute('hidden');
    }

    onAvailabilityChange(event) {
      const activeCandidate = this.candidates[this.activeIndex];
      if (!activeCandidate || !activeCandidate.contains(event.target)) return;

      const availability = this.availability(activeCandidate);
      if (availability === 'ready') {
        this.showCandidate(activeCandidate);
      } else if (availability === 'empty') {
        if (window.Shopify?.designMode && this.selectedBlockId === activeCandidate.dataset.carouselFallbackBlockId) {
          this.showEditorEmpty(activeCandidate);
        } else {
          this.activateCandidate(this.activeIndex + 1);
        }
      }
    }

    onBlockSelect(event) {
      if (!window.Shopify?.designMode) return;
      const candidate = event.target.closest(candidateSelector);
      if (!candidate || !this.contains(candidate)) return;

      this.selectedBlockId = candidate.dataset.carouselFallbackBlockId || '';
      this.activateCandidate(this.candidates.indexOf(candidate));
    }

    onBlockDeselect() {
      if (!window.Shopify?.designMode) return;
      this.selectedBlockId = '';
      this.activateCandidate(0);
    }
  }

  if (!customElements.get('carousel-fallback')) {
    customElements.define('carousel-fallback', CarouselFallback);
  }

})();
